import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import valid from 'card-validator';
import useCieloTDCContext from 'components/cielo/CieloTDCContext/useCieloTDCContext';
import Installments from 'components/purchase/Installments';
import usePurchase from 'hooks/store/usePurchase';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import { getExpirationMonths, getExpirationYears } from 'utils/formRenderers';
import { BRAZILIAN_STATES, CARD_BRANDS } from 'utils/cielo';
import CieloWebViewFormInput from './CieloWebViewFormInput';
import CieloWebViewFormSelect from './CieloWebViewFormSelect';

/**
 * Unified Cielo TDC form for WebView usage.
 * Supports BOTH 3DS authentication and Silent Order Post tokenization simultaneously.
 * Replicates the behavior of CieloTDCForm but adapted for WebView.
 *
 * The form includes all necessary hidden inputs for both flows:
 * - 3DS: bpmpi_* classes and hidden inputs for authentication
 * - SOP: bp-sop-* classes for tokenization
 *
 * CieloTDCProvider determines which scripts to load and which flow to use
 * based on environment configuration (enableThreeDS, enableSilentOrderPost).
 *
 * @param {object} props - Component props
 * @param {object} props.purchaseData - Purchase information including token and total
 */
const CieloWebViewForm = ({ purchaseData }) => {
  const { t } = useTranslation('payment');
  const { accessToken, form, config, resetBillingAddress } = useCieloTDCContext();
  const purchase = usePurchase();
  const features = useWhitelabelFeatures() || {};
  const isBillingAddressEnabled = Boolean(features.CIELO_BILLING_ADDRESS_ENABLED);
  // User toggle (Formik field). The billing address is ALWAYS prefilled and shown
  // (seeded in CieloTDCProvider from the purchase/ticket meta); this toggle now
  // controls editability AND whether we resend the address on submit:
  //   off -> fields read-only, address NOT sent (backend keeps the ticket-meta address)
  //   on  -> fields editable,  address sent      (backend supersedes the ticket meta)
  // Keeping it off by default guarantees the default (ticket) address can't be tampered.
  // Only meaningful when the feature flag above is on.
  const includeBillingAddress = form.values?.includeBillingAddress !== false;

  // Accordion state for the address fields. It is independent of the edit toggle but
  // *defaults* from it: toggle off -> collapsed (fields hidden, user can tap the header
  // to reveal the read-only default address), toggle on -> expanded. Flipping the toggle
  // syncs this (see the switch onChange), yet the user can still fold/unfold at will.
  const [isAddressExpanded, setIsAddressExpanded] = React.useState(includeBillingAddress);
  // Focus state for the visually-hidden switch input, so keyboard users get a
  // visible focus ring on the track (the real <input> has opacity 0).
  const [isSwitchFocused, setIsSwitchFocused] = React.useState(false);

  const { token = 'webview-purchase' } = purchaseData || {};
  const total = purchase.total || parseFloat(purchase.amount) || purchaseData?.total || 0;
  const monthlySelectedPlan = purchase.monthlySelectedPlan || 1;

  const totalInCentsInteger = Math.round(total * 100);

  /**
   * Sets the card brand from a manual selection and flags it as user-overridden
   * so the card-number auto-detection stops updating it.
   * @param {object} e - Change event from the brand selector.
   */
  const handleCardBrandChange = (e) => {
    form?.setFieldValue('cardBrand', e.target.value);
    form?.setFieldValue('cardBrandManual', true);
  };

  // Brand selector (auto-detected from the card number, manually overridable).
  const cardBrandSelector = isBillingAddressEnabled ? (
    <CieloWebViewFormSelect
      id="cardBrand"
      name="cardBrand"
      label={t('card_brand')}
      options={CARD_BRANDS}
      value={form.values?.cardBrand || ''}
      disabled={false}
      onBlur={form.handleBlur}
      onChange={handleCardBrandChange}
      hasError={Boolean(form.errors?.cardBrand && form.touched?.cardBrand)}
      isValid={Boolean(!form.errors?.cardBrand && form.touched?.cardBrand)}
    />
  ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/** Hidden inputs required by Cielo 3DS (always included for compatibility) */}
      <input type="hidden" name="authEnabled" className="bpmpi_auth" value="true" />
      <input type="hidden" name="accessToken" className="bpmpi_accesstoken" value={accessToken} />
      <input type="hidden" name="orderNumber" className="bpmpi_ordernumber" value={token} />
      <input
        type="hidden"
        name="currency"
        className="bpmpi_currency"
        value={config?.currency || ''}
      />
      <input
        type="hidden"
        name="amount"
        className="bpmpi_totalamount"
        value={totalInCentsInteger}
      />
      <input
        type="hidden"
        name="installments"
        className="bpmpi_installments"
        value={String(monthlySelectedPlan)}
      />
      <input
        type="hidden"
        name="merchantUrl"
        className="bpmpi_merchant_url"
        value={config?.merchantUrl || ''}
      />
      <input type="hidden" name="paymentMethod" className="bpmpi_paymentmethod" value="credit" />

      {/**
       * Installments selector. Same component and same position as the checkout form
       * (CieloTDCForm) — it is connected, so it reads the plans getPurchase already put in the
       * store and writes the choice back to purchase.monthlySelectedPlan, which is what both the
       * bpmpi_installments field above and the created payment read.
       */}
      {features.INSTALLMENTS_ENABLED && <Installments />}

      {/** User input fields with both 3DS and SOP CSS classes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Card Holder Name */}
        <CieloWebViewFormInput
          id="holderName"
          name="holderName"
          label={t('card_owner_name')}
          className="bp-sop-cardholdername"
          value={form.values?.holderName || ''}
          disabled={false}
          onBlur={form.handleBlur}
          onChange={form.handleChange}
          hasError={Boolean(form.errors?.holderName && form.touched?.holderName)}
          isValid={Boolean(!form.errors?.holderName && form.touched?.holderName)}
        />

        {/* Card Number */}
        <CieloWebViewFormInput
          id="cardNumber"
          name="cardNumber"
          label={t('card_number')}
          className="bpmpi_cardnumber bp-sop-cardnumber"
          value={form.values?.cardNumber || ''}
          disabled={false}
          onBlur={form.handleBlur}
          onChange={(e) => {
            const digitsOnly = e.target.value.replace(/\D/g, '');
            form?.setFieldValue('cardNumber', digitsOnly);
            // Keep the brand selector in sync with the typed number unless the
            // user has manually overridden it.
            if (!form.values?.cardBrandManual) {
              const detectedCard = valid.number(digitsOnly).card;
              const detectedBrand = detectedCard ? detectedCard.type : '';
              form?.setFieldValue('cardBrand', detectedBrand);
            }
          }}
          hasError={Boolean(form.errors?.cardNumber && form.touched?.cardNumber)}
          isValid={Boolean(!form.errors?.cardNumber && form.touched?.cardNumber)}
        />

        {/* Card Brand (auto-detected, manually overridable) */}
        {cardBrandSelector}

        {/* Expiration Date */}
        <div>
          <label
            htmlFor="expirationDate"
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#1f2937',
            }}
          >
            {t('expiration_date')}
          </label>
          <fieldset
            id="expirationDate"
            style={{
              display: 'flex',
              gap: '12px',
              border: 'none',
              padding: 0,
              margin: 0,
            }}
          >
            <CieloWebViewFormSelect
              id="expirationMonth"
              name="expirationMonth"
              className="bpmpi_cardexpirationmonth"
              options={getExpirationMonths()}
              value={form.values?.expirationMonth || getExpirationMonths()[0].value}
              disabled={false}
              onBlur={form.handleBlur}
              onChange={form.handleChange}
              hasError={Boolean(form.errors?.expirationMonth && form.touched?.expirationMonth)}
              isValid={Boolean(!form.errors?.expirationMonth && form.touched?.expirationMonth)}
            />
            <CieloWebViewFormSelect
              id="expirationYear"
              name="expirationYear"
              className="bpmpi_cardexpirationyear"
              options={getExpirationYears().map((year) => ({
                display: year,
                value: year,
              }))}
              value={form.values?.expirationYear || String(getExpirationYears()[0])}
              disabled={false}
              onBlur={form.handleBlur}
              onChange={form.handleChange}
              hasError={Boolean(form.errors?.expirationYear && form.touched?.expirationYear)}
              isValid={Boolean(!form.errors?.expirationYear && form.touched?.expirationYear)}
            />
          </fieldset>
        </div>

        {/* CVV */}
        <CieloWebViewFormInput
          id="cvv2"
          name="cvv2"
          label={t('card_CVV')}
          className="bp-sop-cardcvv"
          value={form.values?.cvv2 || ''}
          disabled={false}
          onBlur={form.handleBlur}
          onChange={form.handleChange}
          hasError={Boolean(form.errors?.cvv2 && form.touched?.cvv2)}
          isValid={Boolean(!form.errors?.cvv2 && form.touched?.cvv2)}
        />
      </div>

      {/* Billing Address Section */}
      {isBillingAddressEnabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Section header row: an accordion trigger (title + chevron) plus the edit
              toggle. The address fields below collapse/expand independently of the toggle
              (see isAddressExpanded); the toggle only controls editability + resend. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              borderTop: '1px solid #e5e7eb',
              paddingTop: '20px',
            }}
          >
            {/* Accordion trigger: reveals/hides the address fields. Works even when the
                toggle is off, letting the user peek at the read-only default address. */}
            <button
              type="button"
              onClick={() => setIsAddressExpanded((expanded) => !expanded)}
              aria-expanded={isAddressExpanded}
              aria-controls="billingAddressFields"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: 1,
                background: 'none',
                border: 'none',
                padding: 0,
                margin: 0,
                fontSize: '16px',
                fontWeight: '700',
                color: '#1f2937',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              {t('billing_address')}
              {/* Chevron: points down when collapsed, up when expanded. */}
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRight: '2px solid #6b7280',
                  borderBottom: '2px solid #6b7280',
                  transform: isAddressExpanded ? 'rotate(-135deg)' : 'rotate(45deg)',
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
            </button>

            {/* Edit toggle (iOS-style switch). Off = read-only + not resent;
                On = editable + resent. Flipping it also sets the default accordion state. */}
            <label
              htmlFor="includeBillingAddress"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  flexShrink: 0,
                  width: '44px',
                  height: '24px',
                }}
              >
                <input
                  type="checkbox"
                  role="switch"
                  id="includeBillingAddress"
                  name="includeBillingAddress"
                  checked={includeBillingAddress}
                  aria-checked={includeBillingAddress}
                  aria-label={t('billing_address_include')}
                  onFocus={() => setIsSwitchFocused(true)}
                  onBlur={() => setIsSwitchFocused(false)}
                  onChange={(e) => {
                    const { checked } = e.target;
                    if (checked) {
                      // ON unlocks the fields for editing.
                      form?.setFieldValue('includeBillingAddress', checked);
                    } else {
                      // OFF restores the registered address AND flips the flag off in a
                      // single atomic update (see resetBillingAddress) — doing them as two
                      // separate Formik calls raced and left the toggle stuck on.
                      resetBillingAddress();
                    }
                    // Reveal the fields when turning on, collapse them when turning off.
                    setIsAddressExpanded(checked);
                  }}
                  style={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    margin: 0,
                    padding: 0,
                  }}
                />
                {/* Track */}
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '999px',
                    backgroundColor: includeBillingAddress ? '#2563eb' : '#d1d5db',
                    boxShadow: isSwitchFocused ? '0 0 0 3px rgba(37, 99, 235, 0.4)' : 'none',
                    transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  }}
                />
                {/* Knob */}
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: '2px',
                    left: '2px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    transform: includeBillingAddress ? 'translateX(20px)' : 'translateX(0)',
                    transition: 'transform 0.2s ease-in-out',
                  }}
                />
              </span>
            </label>
          </div>

          {/* Address fields are prefilled (INS-957) and read-only until the user turns the
              toggle on (disabled = !includeBillingAddress). The container stays mounted so
              the header button's aria-controls target always resolves; only its visibility
              is toggled via isAddressExpanded. */}
          <div
            id="billingAddressFields"
            hidden={!isAddressExpanded}
            style={{
              display: isAddressExpanded ? 'flex' : 'none',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            {/* CEP */}
            <CieloWebViewFormInput
              id="cep"
              name="cep"
              label={t('billing_cep')}
              value={form.values?.cep || ''}
              disabled={!includeBillingAddress}
              onBlur={form.handleBlur}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 8);
                form?.setFieldValue('cep', digitsOnly);
              }}
              hasError={Boolean(form.errors?.cep && form.touched?.cep)}
              isValid={Boolean(!form.errors?.cep && form.touched?.cep)}
            />

            {/* Estado */}
            <CieloWebViewFormSelect
              id="estado"
              name="estado"
              label={t('billing_state')}
              options={BRAZILIAN_STATES}
              value={form.values?.estado || ''}
              disabled={!includeBillingAddress}
              onBlur={form.handleBlur}
              onChange={form.handleChange}
              hasError={Boolean(form.errors?.estado && form.touched?.estado)}
              isValid={Boolean(!form.errors?.estado && form.touched?.estado)}
            />

            {/* Cidade */}
            <CieloWebViewFormInput
              id="cidade"
              name="cidade"
              label={t('billing_city')}
              value={form.values?.cidade || ''}
              disabled={!includeBillingAddress}
              onBlur={form.handleBlur}
              onChange={form.handleChange}
              hasError={Boolean(form.errors?.cidade && form.touched?.cidade)}
              isValid={Boolean(!form.errors?.cidade && form.touched?.cidade)}
            />

            {/* Rua */}
            <CieloWebViewFormInput
              id="rua"
              name="rua"
              label={t('billing_street')}
              value={form.values?.rua || ''}
              disabled={!includeBillingAddress}
              onBlur={form.handleBlur}
              onChange={form.handleChange}
              hasError={Boolean(form.errors?.rua && form.touched?.rua)}
              isValid={Boolean(!form.errors?.rua && form.touched?.rua)}
            />

            <div style={{ display: 'flex', gap: '12px' }}>
              {/* Número */}
              <CieloWebViewFormInput
                id="numero"
                name="numero"
                label={t('billing_number')}
                value={form.values?.numero || ''}
                disabled={!includeBillingAddress}
                onBlur={form.handleBlur}
                onChange={form.handleChange}
                hasError={Boolean(form.errors?.numero && form.touched?.numero)}
                isValid={Boolean(!form.errors?.numero && form.touched?.numero)}
              />

              {/* Complemento */}
              <CieloWebViewFormInput
                id="complemento"
                name="complemento"
                label={t('billing_complement')}
                value={form.values?.complemento || ''}
                disabled={!includeBillingAddress}
                onBlur={form.handleBlur}
                onChange={form.handleChange}
                hasError={false}
                isValid={false}
              />
            </div>

            {/* Bairro */}
            <CieloWebViewFormInput
              id="bairro"
              name="bairro"
              label={t('billing_neighborhood')}
              value={form.values?.bairro || ''}
              disabled={!includeBillingAddress}
              onBlur={form.handleBlur}
              onChange={form.handleChange}
              hasError={Boolean(form.errors?.bairro && form.touched?.bairro)}
              isValid={Boolean(!form.errors?.bairro && form.touched?.bairro)}
            />
          </div>
        </div>
      )}

      {/** Hidden inputs for SOP (always included for compatibility) */}
      <input
        type="hidden"
        className="bp-sop-cardexpirationdate"
        value={`${form.values?.expirationMonth || ''}/${form.values?.expirationYear || ''}`}
      />
      <input type="hidden" className="bp-sop-cardtype" value="creditCard" />
    </div>
  );
};

CieloWebViewForm.propTypes = {
  purchaseData: PropTypes.shape({
    token: PropTypes.string,
    total: PropTypes.number,
  }),
};

CieloWebViewForm.defaultProps = {
  purchaseData: null,
};

export default CieloWebViewForm;
